import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl';
import type { IconComponent } from '@alltrails/denali/components/Icon';
import CalendarFilled from '@alltrails/denali/icons/CalendarFilled';
import CampingFilled from '@alltrails/denali/icons/CampingFilled';
import DropletFilled from '@alltrails/denali/icons/DropletFilled';
import PlaceFilled from '@alltrails/denali/icons/PlaceFilled';
import PlaceMountainFilled from '@alltrails/denali/icons/PlaceMountainFilled';
import SignpostFilled from '@alltrails/denali/icons/SignpostFilled';
import type { WaypointCategoryUid } from '@alltrails/types/Waypoint';

type WaypointCategoryConfig = {
  id: number;
  icon: IconComponent;
  name: string;
  namePlural: string;
};

const categoryMessages = defineMessages({
  camping: { defaultMessage: 'Overnight spot' },
  water: { defaultMessage: 'Water source' },
  navigation: { defaultMessage: 'Navigation' },
  landmark: { defaultMessage: 'Landmark' },
  'event-marker': { defaultMessage: 'Event marker' },
  other: { defaultMessage: 'Other' }
});

const categoryMessagesPlural = defineMessages({
  camping: { defaultMessage: 'Overnight spots' },
  water: { defaultMessage: 'Water sources' },
  navigation: { defaultMessage: 'Navigation' },
  landmark: { defaultMessage: 'Landmarks' },
  'event-marker': { defaultMessage: 'Event markers' },
  other: { defaultMessage: 'Other' }
});

const WAYPOINT_CATEGORY_CONFIGS: Record<
  WaypointCategoryUid,
  Pick<WaypointCategoryConfig, 'id' | 'icon'> & { name: MessageDescriptor; namePlural: MessageDescriptor }
> = {
  other: {
    id: 1,
    icon: PlaceFilled,
    name: categoryMessages.other,
    namePlural: categoryMessagesPlural.other
  },
  camping: {
    id: 2,
    icon: CampingFilled,
    name: categoryMessages.camping,
    namePlural: categoryMessagesPlural.camping
  },
  water: {
    id: 3,
    icon: DropletFilled,
    name: categoryMessages.water,
    namePlural: categoryMessagesPlural.water
  },
  navigation: {
    id: 4,
    icon: SignpostFilled,
    name: categoryMessages.navigation,
    namePlural: categoryMessagesPlural.navigation
  },
  landmark: {
    id: 5,
    icon: PlaceMountainFilled,
    name: categoryMessages.landmark,
    namePlural: categoryMessagesPlural.landmark
  },
  'event-marker': {
    id: 6,
    icon: CalendarFilled,
    name: categoryMessages['event-marker'],
    namePlural: categoryMessagesPlural['event-marker']
  }
};

export const DEFAULT_WAYPOINT_CATEGORY_UID: WaypointCategoryUid = 'other';

export const WAYPOINT_CATEGORY_UIDS: WaypointCategoryUid[] = ['camping', 'water', 'navigation', 'landmark', 'event-marker', 'other'];

export function getWaypointCategoryConfig(intl: IntlShape, uid?: string | null): WaypointCategoryConfig {
  if (uid && uid in WAYPOINT_CATEGORY_CONFIGS) {
    const { name, namePlural, ...rest } = WAYPOINT_CATEGORY_CONFIGS[uid as WaypointCategoryUid];
    return {
      ...rest,
      name: intl.formatMessage(name),
      namePlural: intl.formatMessage(namePlural)
    };
  }
  const { name, namePlural, ...rest } = WAYPOINT_CATEGORY_CONFIGS[DEFAULT_WAYPOINT_CATEGORY_UID];
  return {
    ...rest,
    name: intl.formatMessage(name),
    namePlural: intl.formatMessage(namePlural)
  };
}

export default WAYPOINT_CATEGORY_CONFIGS;
